import { KeyboardEvent, useState } from "react";
import { ChatMessage, SupportData } from "../dashboardOverview.types";
import { ChatIcon } from "./DashboardIcons";

export interface LiveChatWidgetProps {
	support: SupportData;
	/** Called whenever the person sends a message from the panel. */
	onSendMessage?: (message: string) => void;
}

/**
 * Floating support entry point for the dashboard. Collapsed, it shows the
 * chat bubble with a small "online" preview tooltip. Clicking the bubble
 * opens a full conversation panel — header bar, message thread (seeded
 * from `support.initialMessages`), and a composer — matching the
 * SimplePay-style live chat reference.
 *
 * Sending a message appends it to the thread locally (so the UI feels
 * real) and calls `onSendMessage` so it can be wired to a real chat/support
 * provider (Intercom, Zendesk, your own backend, etc.) without touching
 * this component's markup.
 */
export const LiveChatWidget = ({ support, onSendMessage }: LiveChatWidgetProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [message, setMessage] = useState("");
	// ===== CHANGED: the panel now renders a real conversation thread instead
	// of a bare textarea — seeded from support.initialMessages, with sent
	// messages appended locally (copy this state + handleSend) =====
	const [messages, setMessages] = useState<ChatMessage[]>(support.initialMessages);

	const handleSend = () => {
		const trimmed = message.trim();
		if (!trimmed) return;
		const newMessage: ChatMessage = {
			id: `local-${Date.now()}`,
			sender: "user",
			text: trimmed,
			timeLabel: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
		};
		setMessages((current) => [...current, newMessage]);
		onSendMessage?.(trimmed);
		setMessage("");
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			handleSend();
		}
	};

	return (
		<>
			{/* ===== CHANGED: expanded panel is now a full conversation thread
			    (copy this whole block) ===== */}
			{isOpen ? (
				<div className="cd-chat-panel cd-chat-panel--conversation" role="dialog" aria-label="Live support">
					<div className="cd-chat-panel__header cd-chat-panel__header--filled">
						<div className="cd-chat-panel__title cd-chat-panel__title--on-fill">
							<span className="cd-online-dot cd-online-dot--on-fill" />
							<span>
								{support.chatTitle}
								<span className="cd-chat-panel__status">{support.chatStatusLabel}</span>
							</span>
						</div>
						<button
							type="button"
							className="cd-chat-panel__close cd-chat-panel__close--on-fill"
							aria-label="Close live support"
							onClick={() => setIsOpen(false)}
						>
							&times;
						</button>
					</div>

					<div className="cd-chat-thread">
						{messages.map((msg) => (
							<div key={msg.id} className={`cd-chat-message cd-chat-message--${msg.sender}`}>
								{msg.sender === "agent" && msg.senderLabel && (
									<div className="cd-chat-message__sender">{msg.senderLabel}</div>
								)}
								<div className="cd-chat-message__bubble">{msg.text}</div>
								<div className="cd-chat-message__time">{msg.timeLabel}</div>
							</div>
						))}
					</div>

					<div className="cd-chat-panel__composer cd-chat-panel__composer--row">
						<input
							type="text"
							className="cd-chat-panel__input"
							placeholder="Type a message..."
							value={message}
							onChange={(event) => setMessage(event.target.value)}
							onKeyDown={handleKeyDown}
						/>
						<button type="button" className="cd-chat-send-button" aria-label="Send message" onClick={handleSend}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<path d="M4 12L20 4L13 20L11 13L4 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
							</svg>
						</button>
					</div>
				</div>
			) : (
				<div className="cd-chat-preview" role="status">
					<div className="cd-chat-preview__eyebrow">
						<span className="cd-online-dot" />
						LIVE SUPPORT &middot; ONLINE
					</div>
					<div className="cd-chat-preview__text">{support.responseTimeNote}</div>
				</div>
			)}

			<button
				type="button"
				className="cd-live-chat-button"
				aria-label={isOpen ? "Close live chat" : "Open live chat"}
				onClick={() => setIsOpen((open) => !open)}
			>
				<ChatIcon />
			</button>
		</>
	);
};
